/**
 * InsuranceLoansAnalytics — what the estate financed, and what the drivers paid for themselves.
 *
 * TWO CORRECTIONS, BOTH [D-B46 R9]. The cards summed every `insurance_loans` row as an advance, so
 * the 12 paid-in-full years (GHS 45,883.96 of premium) were counted as money Primelane put in, and
 * their rates — 10 of the 12 at zero or NULL — dragged the average cost of borrowing down. And
 * "Total Advanced" was `Σ A` gross of deposits, while the owner's rule (2026-09-01) is that what
 * Primelane advanced is the premium LESS what the driver paid: `A − D`.
 *
 * THE BASIS IS `insurances.starts_at`, NOT THE LOAN'S `created_at`. The policy start is the year
 * the premium is FOR; `created_at` is when a clerk typed the row, and 212 of 308 production loans
 * land in a different month on the two. `get_contracts_analytics` and `get_contracts_monthly_trend`
 * both bucket on `starts_at`, so this is also what stops the estate KPI and this page disagreeing.
 *
 * ONE READ FOR THE WHOLE PAGE. Both tabs use `PREMIUM_YEARS_KEY`, so switching between Analytics
 * and Records is a cache hit rather than a second fetch of the same table.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { BadgeCheck, Calendar, Landmark, Percent } from 'lucide-react';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Legend,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import { MonthRangePicker } from '@/components/MonthRangePicker';
import { format, subMonths, startOfMonth, endOfMonth, eachMonthOfInterval } from 'date-fns';
import { businessToday, formatCurrency } from '@/lib/format';
import {
    PREMIUM_YEARS_KEY,
    fetchPremiumYears,
    premiumMonthly,
    premiumSummary,
    withinPolicyWindow,
} from './premiumLedger';

export default function InsuranceLoansAnalytics() {
    const [monthRange, setMonthRange] = useState({
        from: startOfMonth(subMonths(businessToday(), 11)),
        to: endOfMonth(businessToday()),
    });

    const fromDate = format(monthRange.from, 'yyyy-MM-dd');
    const toDate = format(monthRange.to, 'yyyy-MM-dd');

    const { data: premiumYears, isLoading, error } = useQuery({
        queryKey: PREMIUM_YEARS_KEY,
        queryFn: fetchPremiumYears,
    });

    const { summary, monthlyData } = useMemo(() => {
        const inWindow = (premiumYears ?? []).filter((row) =>
            withinPolicyWindow(row, fromDate, toDate),
        );
        const monthKeys = eachMonthOfInterval({ start: monthRange.from, end: monthRange.to }).map(
            (month) => format(month, 'yyyy-MM'),
        );

        return {
            summary: premiumSummary(inWindow),
            monthlyData: premiumMonthly(inWindow, monthKeys).map((point) => ({
                ...point,
                // Labelled off the key's own parts — never `new Date(key + '-01')`, which reads
                // UTC midnight in the viewer's zone and puts every bar a month early west of
                // Greenwich.
                month: format(
                    new Date(Number(point.monthKey.slice(0, 4)), Number(point.monthKey.slice(5, 7)) - 1, 1),
                    'MMM yy',
                ),
            })),
        };
    }, [premiumYears, fromDate, toDate, monthRange.from, monthRange.to]);

    // A FAILED OR TRUNCATED READ STATES NOTHING [R11]. Every card here is a Σ or a count over the
    // whole estate, so rendering them off a response the server cut short would print four wrong
    // totals under confident headings with nothing to mark them. `fetchPremiumYears` throws on
    // `rows.length < count`; this is where an admin reads why.
    if (error) {
        return <p className="text-center text-destructive py-8">{error.message}</p>;
    }

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
                </div>
                <Skeleton className="h-80" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-end">
                <MonthRangePicker monthRange={monthRange} onMonthRangeChange={setMonthRange} />
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Advanced</CardTitle>
                        <Landmark className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{formatCurrency(summary.totalAdvanced)}</div>
                        <p className="text-xs text-muted-foreground">
                            Premium less what the driver paid, over {summary.advanceCount} advances
                        </p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Paid in Full</CardTitle>
                        <BadgeCheck className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{formatCurrency(summary.paidInFullTotal)}</div>
                        <p className="text-xs text-muted-foreground">
                            {summary.paidInFullCount} premium years the driver paid for
                        </p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Avg Interest Rate</CardTitle>
                        <Percent className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{summary.avgRate.toFixed(1)}%</div>
                        <p className="text-xs text-muted-foreground">Across advances only</p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Premium Years</CardTitle>
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{summary.rowCount}</div>
                        <p className="text-xs text-muted-foreground">
                            {summary.advanceCount} advanced · {summary.paidInFullCount} paid in full
                        </p>
                    </CardContent>
                </Card>
            </div>

            <Card className="border border-border/40">
                <CardHeader>
                    <CardTitle>Monthly Premium Years</CardTitle>
                    <CardDescription>By policy start — what Primelane financed, and what drivers paid in full</CardDescription>
                </CardHeader>
                <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                        {/* ONE STACK, TWO SERIES. A shared `stackId` is what makes the bars add up
                            to the month's whole premium volume; two ids would draw two charts on
                            one axis and overstate every month by the overlap. */}
                        <AreaChart data={monthlyData}>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                            <XAxis dataKey="month" className="text-xs" />
                            <YAxis className="text-xs" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                            <Tooltip
                                formatter={(value: number) => formatCurrency(value)}
                                contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                            />
                            <Legend />
                            <Area type="monotone" dataKey="financed" stackId="premium" stroke="hsl(var(--chart-1))" fill="hsl(var(--chart-1))" fillOpacity={0.6} name="Financed" />
                            <Area type="monotone" dataKey="paidInFull" stackId="premium" stroke="hsl(var(--chart-2))" fill="hsl(var(--chart-2))" fillOpacity={0.6} name="Paid in full" />
                        </AreaChart>
                    </ResponsiveContainer>
                </CardContent>
            </Card>
        </div>
    );
}
